import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import automatedPaymentService from '../../services/automatedPaymentService';
import PaymentQueueTable from './components/PaymentQueueTable';
import PaymentStatisticsCard from './components/PaymentStatisticsCard';
import BillingNotificationsPanel from './components/BillingNotificationsPanel';
import RetryLogModal from './components/RetryLogModal';

const AutomatedPaymentMonitoringDashboard = () => {
  const [payments, setPayments] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [companyId, setCompanyId] = useState(null);

  useEffect(() => {
    fetchCompanyId();
  }, []);

  useEffect(() => {
    if (companyId) {
      fetchData();
    }
  }, [companyId]);

  const fetchCompanyId = async () => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('company_id')
          ?.eq('id', user?.id)
          ?.single();
        setCompanyId(profile?.company_id);
      }
    } catch (err) {
      console.error('Error fetching company ID:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [paymentsData, statsData, notificationsData] = await Promise.all([
        automatedPaymentService?.getAutomatedPayments(companyId),
        automatedPaymentService?.getPaymentStatistics(companyId),
        automatedPaymentService?.getBillingNotifications(companyId)
      ]);

      setPayments(paymentsData);
      setStatistics(statsData);
      setNotifications(notificationsData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayments = async () => {
    try {
      setProcessing(true);
      setError(null);

      const result = await automatedPaymentService?.processAutomatedPayments();
      
      alert(`Processed ${result?.processed || 0} payments successfully`);
      await fetchData();
    } catch (err) {
      console.error('Error processing payments:', err);
      setError(err?.message || 'Failed to process payments');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewRetryLogs = (payment) => {
    setSelectedPayment(payment);
  };

  const handleCloseRetryLogs = () => {
    setSelectedPayment(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Automated Payment Monitoring | PNG HR Management</title>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Automated Payment Monitoring</h1>
              <p className="mt-2 text-gray-600">
                Track day 8 trial expiration charges, retry attempts, and billing notifications
              </p>
            </div>
            <button
              onClick={handleProcessPayments}
              disabled={processing}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <span>⚙️</span>
                  Process Pending Payments
                </>
              )}
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
            <div className="flex items-center">
              <span className="text-red-500 text-xl mr-3">❌</span>
              <div>
                <h3 className="text-red-800 font-semibold">Error</h3>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <PaymentStatisticsCard
              title="Total Payments"
              value={statistics?.totalPayments}
              subtitle="" 
              icon="💳"
              color="blue"
            />
            <PaymentStatisticsCard
              title="Successful"
              value={statistics?.successfulPayments}
              subtitle={`${statistics?.successRate}% success rate`}
              icon="✅"
              color="green"
            />
            <PaymentStatisticsCard
              title="Failed/Exhausted"
              value={statistics?.failedPayments + statistics?.exhaustedPayments}
              subtitle={`${statistics?.failedPayments} failed, ${statistics?.exhaustedPayments} exhausted`}
              icon="❌"
              color="red"
            />
            <PaymentStatisticsCard
              title="Total Revenue"
              value={automatedPaymentService?.formatAmount(statistics?.successfulAmount)}
              subtitle={`From ${statistics?.successfulPayments} payments`}
              icon="💰"
              color="green"
            />
          </div>
        )}

        {/* Payment Queue Table */}
        <div className="mb-8">
          <PaymentQueueTable
            payments={payments}
            onViewRetryLogs={handleViewRetryLogs}
            onRefresh={fetchData}
          />
        </div>

        {/* Billing Notifications Panel */}
        <div>
          <BillingNotificationsPanel
            notifications={notifications}
            onRefresh={fetchData}
          />
        </div>

        {/* Retry Log Modal */}
        {selectedPayment && (
          <RetryLogModal
            payment={selectedPayment}
            onClose={handleCloseRetryLogs}
          />
        )}
      </div>
    </>
  );
};

export default AutomatedPaymentMonitoringDashboard;